// Whole-list operations on the steps recipe (redesign spec §7): the survey
// page saves one step at a time but always sends the whole list, so these
// work on the list and leave the saving to the caller.

import type { ApiError } from "@/api/error";
import type { StepKind } from "@/api/types";
import {
    SPLIT_NEEDS_SCORE,
    type StepFormValue,
    type StepsProblem,
    toStepFormValue,
    validateSteps,
} from "../survey-schema";
import { type SurveyTemplateKey, templateSteps } from "../survey-templates";

type Shape = Pick<StepFormValue, "kind" | "multiMode" | "isMetric">;

function isSplit(step: Shape | undefined): boolean {
    return step?.kind === "multi" && step.multiMode === "split";
}

export function stepPrompt(step: { config: Record<string, unknown> }): string {
    // A split multi-select has no single prompt; its low-score one names it.
    const { prompt, promptNegative } = step.config as { prompt?: string; promptNegative?: string };
    return prompt || promptNegative || "Untitled";
}

/** The one place a step-list problem is put into words — a step's save, a
 *  move, a delete and the create page all show it the same way. */
export function describeStepsProblem(
    problem: StepsProblem,
    numberOf: (index: number) => number = (index) => index + 1,
): string {
    if (problem.index === null) return problem.message;
    const number = numberOf(problem.index);
    return problem.message === SPLIT_NEEDS_SCORE
        ? `Step ${number} is split by score and needs a scored step before it.`
        : `Step ${number}: ${problem.message}`;
}

const STEP_PATH = /^steps\[(\d+)\]/;

/** The server's refusal of a steps PUT as a StepsProblem, from its `steps…`
 *  field errors: `steps[i]…` names a step, `steps` the whole list. The one at
 *  `prefer` (the card asking) wins; else the first. */
export function serverStepsProblem(error: ApiError, prefer?: number): StepsProblem | undefined {
    const problems: StepsProblem[] = [];
    for (const field of error.errors ?? []) {
        const message = field.message.charAt(0).toUpperCase() + field.message.slice(1);
        const at = STEP_PATH.exec(field.path);
        if (at) problems.push({ index: Number(at[1]), message });
        else if (field.path === "steps") problems.push({ index: null, message });
    }
    return problems.find((problem) => prefer !== undefined && problem.index === prefer) ?? problems[0];
}

export function splitFollows(steps: Shape[], index: number): number | null {
    if (!isSplit(steps[index])) return null;
    const scoreAt = steps.findIndex((step) => step.isMetric);
    return scoreAt >= 0 && scoreAt < index ? scoreAt : null;
}

export type MoveResult = { ok: true; steps: StepFormValue[]; carried: number[] } | { ok: false; message: string };

export function moveStep(steps: StepFormValue[], index: number, direction: "up" | "down"): MoveResult {
    const moving = steps[index];
    if (!moving) return { ok: true, steps, carried: [] };

    // The score step carries the split steps directly after it.
    let end = index;
    if (moving.isMetric) {
        while (isSplit(steps[end + 1])) end++;
    }
    const carried = Array.from({ length: end - index }, (_, offset) => index + 1 + offset);

    if (direction === "up") {
        if (index === 0) return { ok: true, steps, carried: [] };
        const scoreAt = splitFollows(steps, index);
        if (scoreAt !== null && scoreAt === index - 1) {
            return {
                ok: false,
                message: `It's split by the score in step ${scoreAt + 1}, so it has to come after it.`,
            };
        }
        const next = [...steps];
        const [displaced] = next.splice(index - 1, 1);
        next.splice(end, 0, displaced as StepFormValue);
        return { ok: true, steps: next, carried };
    }

    if (end >= steps.length - 1) return { ok: true, steps, carried: [] };
    const next = [...steps];
    const [displaced] = next.splice(end + 1, 1);
    next.splice(index, 0, displaced as StepFormValue);
    return { ok: true, steps: next, carried };
}

export function removalProblem(steps: StepFormValue[], index: number): string | null {
    const remaining = steps.filter((_, position) => position !== index);
    const problem = validateSteps({ steps: remaining });
    if (!problem) return null;
    // Name the step as it's numbered now, not as it would be after the removal.
    return describeStepsProblem(problem, (at) => (at >= index ? at + 1 : at) + 1);
}

export function starterSteps(key: SurveyTemplateKey): StepFormValue[] {
    return templateSteps(key)
        .map((step, position) =>
            toStepFormValue({
                id: "",
                position,
                kind: step.kind as StepKind,
                isMetric: step.isMetric ?? false,
                required: step.required ?? false,
                config: step.config,
                answerCount: 0,
            }),
        )
        .map((step) => ({ ...step, stepId: null }));
}
